/**
 * 브랜드별 매장 목록 조회 훅
 */
import { useState, useEffect, useCallback } from 'react';
import { organizationApi } from '@/lib/api/organizations';
import type { Store, OrganizationSummary } from '@/lib/api/types';
import { logger } from '@/utils/logger';

interface BrandSummary {
  id: number;
  slug: string;
  name: string;
}

interface UseBrandStoresResult {
  stores: Store[];
  organization: OrganizationSummary | null;
  brand: BrandSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBrandStores(
  orgParam: string | undefined,
  brandParam: string | undefined
): UseBrandStoresResult {
  const [stores, setStores] = useState<Store[]>([]);
  const [organization, setOrganization] = useState<OrganizationSummary | null>(null);
  const [brand, setBrand] = useState<BrandSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStores = useCallback(async () => {
    if (!orgParam || !brandParam) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await organizationApi.getStoresByBrand(orgParam, brandParam);
      if (response.success) {
        setStores(response.data.stores);
        setOrganization(response.data.organization);
        setBrand(response.data.brand);
      } else {
        setError('매장 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      logger.error('브랜드별 매장 목록 조회 오류:', err);
      setError(err instanceof Error ? err.message : '매장 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [orgParam, brandParam]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  return {
    stores,
    organization,
    brand,
    loading,
    error,
    refetch: fetchStores,
  };
}
